import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { app } from 'services/firebase';
import { AuthenticationMethod, FormStatus } from 'types';
import { logger } from 'utils/secureLogger';

const auth = getAuth(app);

interface AuthContextType {
  // Core auth state
  user: User | null;
  tokenIsValid: boolean | null;
  tokenValidationStatus: FormStatus;
  authenticationMethod: AuthenticationMethod | null;
  
  // State setters (for internal use)
  setTokenIsValid: (valid: boolean | null) => void;
  setTokenValidationStatus: (status: FormStatus) => void;
  setAuthenticationMethod: (method: AuthenticationMethod | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [tokenIsValid, setTokenIsValid] = useState<boolean | null>(null);
  const [tokenValidationStatus, setTokenValidationStatus] = useState<FormStatus>(FormStatus.IDLE);
  const [authenticationMethod, setAuthenticationMethod] = useState<AuthenticationMethod | null>(null);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setTokenIsValid(!!firebaseUser);
      
      if (firebaseUser) {
        // Determine authentication method
        const providerId = firebaseUser.providerData[0]?.providerId;
        if (providerId === 'google.com') {
          setAuthenticationMethod(AuthenticationMethod.google);
        } else if (providerId === 'password') {
          setAuthenticationMethod(AuthenticationMethod.password);
        } else {
          setAuthenticationMethod(null);
        }
        
        logger.debug('User authenticated', { uid: firebaseUser.uid });
      } else {
        setAuthenticationMethod(null);
        logger.debug('User signed out');
      }
    });

    return unsubscribe;
  }, []);

  const contextValue: AuthContextType = {
    user,
    tokenIsValid,
    tokenValidationStatus,
    authenticationMethod,
    setTokenIsValid,
    setTokenValidationStatus,
    setAuthenticationMethod,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
